import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

type IssuedAmount = { currency: string; issuer: string; value: string };
type XRPLAmount = string | IssuedAmount;
type BookOfferLike = { TakerGets: XRPLAmount; TakerPays: XRPLAmount };

function amountToNumber(amount: XRPLAmount): number {
  if (typeof amount === 'string') return Number(amount) / 1_000_000;
  return Number(amount.value);
}

interface PriceHistoryPoint {
  time: string;
  avgPrice: number;
  xrplDex: number;
  coinbase: number;
}

interface ExchangePrice {
  exchange: string;
  price: number;
  type: 'CEX' | 'DEX' | 'Aggregator';
  fees: string;
  liquidity: string;
  timestamp: number;
}

interface ArbitrageOpportunity {
  buyExchange: string;
  buyPrice: number;
  sellExchange: string;
  sellPrice: number;
  spread: number;
  spreadPercent: number;
  netProfit: number;
  potential10k: number;
}

const ArbitrageDashboard: React.FC = () => {
  const [prices, setPrices] = useState<ExchangePrice[]>([]);
  const [opportunities, setOpportunities] = useState<ArbitrageOpportunity[]>([]);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryPoint[]>([]);
  const [, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const calculateArbitrage = useCallback((priceList: ExchangePrice[]) => {
    const opps: ArbitrageOpportunity[] = [];

    for (let i = 0; i < priceList.length; i++) {
      for (let j = 0; j < priceList.length; j++) {
        if (i !== j && priceList[i].price < priceList[j].price) {
          const buy = priceList[i];
          const sell = priceList[j];
          const spread = sell.price - buy.price;
          const spreadPercent = (spread / buy.price) * 100;

          const buyFee = parseFloat(buy.fees) || 0;
          const sellFee = parseFloat(sell.fees) || 0;
          const netProfit = spreadPercent - buyFee - sellFee;

          if (netProfit > -1) { // Show even marginal opportunities
            const potential10k = ((spread * 10000 / buy.price) * sell.price) - (10000 * (buyFee + sellFee) / 100);

            opps.push({
              buyExchange: buy.exchange,
              buyPrice: buy.price,
              sellExchange: sell.exchange,
              sellPrice: sell.price,
              spread,
              spreadPercent,
              netProfit,
              potential10k
            });
          }
        }
      }
    }

    opps.sort((a, b) => b.netProfit - a.netProfit);
    setOpportunities(opps.slice(0, 5)); // Top 5
  }, []);

  const updatePriceHistory = useCallback((priceList: ExchangePrice[]) => {
    const avgPrice = priceList.reduce((sum, p) => sum + p.price, 0) / priceList.length;
    setPriceHistory(prev => {
      const newHistory: PriceHistoryPoint[] = [...prev, {
        time: new Date().toLocaleTimeString(),
        avgPrice: avgPrice,
        xrplDex: priceList.find(p => p.exchange === 'XRPL DEX')?.price || avgPrice,
        coinbase: priceList.find(p => p.exchange === 'Coinbase')?.price || avgPrice
      }];
      return newHistory.slice(-20); // Keep last 20 points
    });
  }, []);

  const fetchXRPLPrice = useCallback(async (): Promise<number | null> => {
    try {
      const { Client } = await import('xrpl');
      const client = new Client('wss://xrplcluster.com');
      await client.connect();

      const bidBook = await client.request({
        command: 'book_offers',
        taker_gets: { currency: 'USD', issuer: 'rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B' },
        taker_pays: { currency: 'XRP' },
        limit: 1
      });

      const askBook = await client.request({
        command: 'book_offers',
        taker_gets: { currency: 'XRP' },
        taker_pays: { currency: 'USD', issuer: 'rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B' },
        limit: 1
      });

      await client.disconnect();

      if (bidBook.result.offers?.[0] && askBook.result.offers?.[0]) {
        const bid = bidBook.result.offers[0] as unknown as BookOfferLike;
        const ask = askBook.result.offers[0] as unknown as BookOfferLike;

        const bidGets = amountToNumber(bid.TakerGets);
        const bidPays = amountToNumber(bid.TakerPays);
        const bidPrice = bidPays > 0 ? bidGets / bidPays : 0;

        const askGets = amountToNumber(ask.TakerGets);
        const askPays = amountToNumber(ask.TakerPays);
        const askPrice = askGets > 0 ? askPays / askGets : 0;

        return (bidPrice + askPrice) / 2;
      }
      return null;
    } catch (error) {
      console.error('XRPL fetch error:', error);
      return null;
    }
  }, []);

  const fetchAllPrices = useCallback(async () => {
    setIsLoading(true);
    const newPrices: ExchangePrice[] = [];
    const timestamp = Date.now();

    try {
      // XRPL DEX
      const xrplPrice = await fetchXRPLPrice();
      if (xrplPrice) {
        newPrices.push({
          exchange: 'XRPL DEX',
          price: xrplPrice,
          type: 'DEX',
          fees: '0.00%',
          liquidity: 'High',
          timestamp
        });
      }

      // Coinbase
      try {
        const res = await fetch('https://api.coinbase.com/v2/prices/XRP-USD/spot');
        const data = await res.json();
        newPrices.push({
          exchange: 'Coinbase',
          price: parseFloat(data.data.amount),
          type: 'CEX',
          fees: '0.50%',
          liquidity: 'Very High',
          timestamp
        });
      } catch (error) {
        console.warn('Coinbase price fetch failed:', error);
      }

      // Kraken
      try {
        const res = await fetch('https://api.kraken.com/0/public/Ticker?pair=XRPUSD');
        const data = await res.json();
        newPrices.push({
          exchange: 'Kraken',
          price: parseFloat(data.result.XXRPZUSD.c[0]),
          type: 'CEX',
          fees: '0.26%',
          liquidity: 'High',
          timestamp
        });
      } catch (error) {
        console.warn('Kraken price fetch failed:', error);
      }

      // Binance US
      try {
        const res = await fetch('https://api.binance.us/api/v3/ticker/price?symbol=XRPUSD');
        const data = await res.json();
        newPrices.push({
          exchange: 'Binance US',
          price: parseFloat(data.price),
          type: 'CEX',
          fees: '0.10%',
          liquidity: 'Very High',
          timestamp
        });
      } catch (error) {
        console.warn('Binance US price fetch failed:', error);
      }

      // Bitstamp
      try {
        const res = await fetch('https://www.bitstamp.net/api/v2/ticker/xrpusd/');
        const data = await res.json();
        newPrices.push({
          exchange: 'Bitstamp',
          price: parseFloat(data.last),
          type: 'CEX',
          fees: '0.50%',
          liquidity: 'High',
          timestamp
        });
      } catch (error) {
        console.warn('Bitstamp price fetch failed:', error);
      }

      setPrices(newPrices);
      calculateArbitrage(newPrices);
      updatePriceHistory(newPrices);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching prices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [calculateArbitrage, fetchXRPLPrice, updatePriceHistory]);

  useEffect(() => {
    void fetchAllPrices();
    const interval = setInterval(() => {
      void fetchAllPrices();
    }, 30000); // Update every 30s
    return () => clearInterval(interval);
  }, [fetchAllPrices]);

  const sorted = [...prices].sort((a, b) => a.price - b.price);
  const avgPrice = prices.length > 0 ? prices.reduce((sum, p) => sum + p.price, 0) / prices.length : 0;

  return (
    <div style={{ padding: '40px', background: '#000', minHeight: '100vh', color: '#e5e7eb' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '10px', color: '#fff' }}>
            ⚡ XRP Arbitrage Dashboard
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '16px' }}>
            Real-time pricing across major exchanges • Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>

        {/* Price Chart */}
        <div style={{ 
          background: '#111', 
          borderRadius: '12px', 
          padding: '30px', 
          marginBottom: '30px',
          border: '1px solid #222'
        }}>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: '#fff' }}>
            📈 Price Trends (Last 10 Minutes)
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={priceHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="time" stroke="#666" />
              <YAxis stroke="#666" domain={['dataMin - 0.01', 'dataMax + 0.01']} />
              <Tooltip 
                contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Line type="monotone" dataKey="avgPrice" stroke="#10b981" name="Average" strokeWidth={2} />
              <Line type="monotone" dataKey="xrplDex" stroke="#3b82f6" name="XRPL DEX" strokeWidth={2} />
              <Line type="monotone" dataKey="coinbase" stroke="#f59e0b" name="Coinbase" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Current Prices */}
        <div style={{ 
          background: '#111', 
          borderRadius: '12px', 
          padding: '30px', 
          marginBottom: '30px',
          border: '1px solid #222'
        }}>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: '#fff' }}>
            💰 Live Exchange Prices
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
            {sorted.map((price, idx) => {
              const isLowest = idx === 0;
              const isHighest = idx === sorted.length - 1;
              const bgColor = isLowest ? '#064e3b' : isHighest ? '#7f1d1d' : '#1a1a1a';
              const borderColor = isLowest ? '#10b981' : isHighest ? '#ef4444' : '#333';

              return (
                <div key={price.exchange} style={{ 
                  background: bgColor, 
                  padding: '20px', 
                  borderRadius: '8px',
                  border: `2px solid ${borderColor}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontWeight: '600', color: '#fff' }}>{price.exchange}</span>
                    <span style={{ 
                      padding: '4px 8px', 
                      background: '#333', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      color: '#9ca3af'
                    }}>
                      {price.type}
                    </span>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#fff', marginBottom: '10px' }}>
                    ${price.price.toFixed(4)}
                  </div>
                  <div style={{ display: 'flex', gap: '15px', fontSize: '14px', color: '#9ca3af' }}>
                    <span>Fee: {price.fees}</span>
                    <span>Liquidity: {price.liquidity}</span>
                  </div>
                  {isLowest && <div style={{ marginTop: '10px', color: '#10b981', fontSize: '14px', fontWeight: '600' }}>🟢 LOWEST</div>}
                  {isHighest && <div style={{ marginTop: '10px', color: '#ef4444', fontSize: '14px', fontWeight: '600' }}>🔴 HIGHEST</div>}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '20px', padding: '15px', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #333' }}>
            <div style={{ fontSize: '16px', color: '#9ca3af', marginBottom: '5px' }}>Market Average:</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#fff' }}>${avgPrice.toFixed(4)}</div>
          </div>
        </div>

        {/* Arbitrage Opportunities */}
        <div style={{ 
          background: '#111', 
          borderRadius: '12px', 
          padding: '30px',
          border: '1px solid #222'
        }}>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: '#fff' }}>
            🎯 Top Arbitrage Opportunities
          </h2>
          {opportunities.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              No profitable opportunities detected. Markets are efficient right now.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {opportunities.map((opp, idx) => (
                <div key={idx} style={{ 
                  background: opp.netProfit > 0 ? '#064e3b' : '#1a1a1a', 
                  padding: '20px', 
                  borderRadius: '8px',
                  border: `2px solid ${opp.netProfit > 0 ? '#10b981' : '#333'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#fff' }}>
                      {opp.buyExchange} → {opp.sellExchange}
                    </div>
                    <div style={{ 
                      fontSize: '20px', 
                      fontWeight: '700', 
                      color: opp.netProfit > 0 ? '#10b981' : '#ef4444' 
                    }}>
                      {opp.netProfit > 0 ? '+' : ''}{opp.netProfit.toFixed(2)}%
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', fontSize: '14px' }}>
                    <div>
                      <div style={{ color: '#9ca3af', marginBottom: '5px' }}>Buy Price</div>
                      <div style={{ color: '#10b981', fontWeight: '600' }}>${opp.buyPrice.toFixed(4)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#9ca3af', marginBottom: '5px' }}>Sell Price</div>
                      <div style={{ color: '#ef4444', fontWeight: '600' }}>${opp.sellPrice.toFixed(4)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#9ca3af', marginBottom: '5px' }}>Spread</div>
                      <div style={{ color: '#fff', fontWeight: '600' }}>${opp.spread.toFixed(4)} ({opp.spreadPercent.toFixed(2)}%)</div>
                    </div>
                    <div>
                      <div style={{ color: '#9ca3af', marginBottom: '5px' }}>$10K Potential</div>
                      <div style={{ color: opp.potential10k > 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                        ${opp.potential10k.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  {opp.netProfit > 0.5 && (
                    <div style={{ 
                      marginTop: '15px', 
                      padding: '10px', 
                      background: '#10b98133', 
                      borderRadius: '6px',
                      color: '#10b981',
                      fontSize: '14px'
                    }}>
                      ✅ PROFITABLE: Buy on {opp.buyExchange}, transfer XRP, sell on {opp.sellExchange}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={{ 
            marginTop: '30px', 
            padding: '20px', 
            background: '#1e3a8a', 
            borderRadius: '8px',
            border: '1px solid #3b82f6'
          }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff', marginBottom: '10px' }}>
              ⚡ Your XRPL DEX Advantage
            </div>
            <div style={{ color: '#93c5fd', fontSize: '14px' }}>
              XRPL DEX has <strong>0% trading fees</strong>. When arbitrage opportunities arise, you can capture the full spread 
              minus only the selling exchange fee. During high volatility (2-5% spreads), this becomes highly profitable.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArbitrageDashboard;
